import type { PilotStateValue } from "@/features/Generations/context/PilotStateContext";

import { useGenerate } from "@/api/creativesphere";
import {
  useCreatePropagation,
  useStreamPropagate,
  useUpdatePropagation,
} from "@/api/propagation";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

// The setup step's write side: every mutation with its error toast in one
// place, so the component reads as state, data, actions, JSX.
export function usePilotMutations(
  siteId: string,
  setPilotState: PilotStateValue["setPilotState"],
) {
  // Creates the Propagation from the chosen product + blueprint. The backend
  // initializes it in the pilot_generating state and returns its slug.
  const { mutate: createPilot, isPending: isCreatingPilot } =
    useCreatePropagation(siteId, {
      onError: (error) =>
        enqueueErrorSnackbar(
          error,
          "Failed to start generation. Please try again.",
        ),
    });

  // Kicks off the pilot image (POST /v1/generate), alongside the propagation.
  const { mutate: generatePilot, isPending: isGeneratingPilot } = useGenerate(
    siteId,
    {
      onError: (error) =>
        enqueueErrorSnackbar(
          error,
          "Failed to start pilot generation. Please try again.",
        ),
    },
  );

  const { mutate: propagateCollection } = useStreamPropagate(siteId, {
    onError: (error) =>
      enqueueErrorSnackbar(
        error,
        "Failed to propagate the collection. Please try again.",
      ),
  });

  // Re-running over an existing propagation rewrites it in place (name,
  // blueprint, product, pilot image) instead of creating a second row.
  const { mutate: updatePilot, isPending: isUpdatingPilot } =
    useUpdatePropagation(siteId, {
      onSuccess: () =>
        setPilotState((prev) => ({ ...prev, pilotInputsChanged: false })),
      onError: (error) =>
        enqueueErrorSnackbar(
          error,
          "Failed to update the generation. Please try again.",
        ),
    });

  const { mutate: updatePropagationState } = useUpdatePropagation(siteId, {
    onError: (error) =>
      enqueueErrorSnackbar(
        error,
        "Failed to update the propagation. Please try again.",
      ),
  });

  return {
    createPilot,
    isCreatingPilot,
    generatePilot,
    isGeneratingPilot,
    propagateCollection,
    updatePilot,
    isUpdatingPilot,
    updatePropagationState,
  };
}
